import React, { useLayoutEffect, useRef, useState } from 'react';
import { TruncatedInputLabel } from 'styles/InputStyles';

import { FormControl, TextField, TextFieldProps, Tooltip } from '@mui/material';

import MaskedTextInput from 'components/MaskedTextInput';

interface CustomInputProps extends Omit<TextFieldProps, 'variant'> {
  label: string;
  id: string;
  name: string;
  mask?: string | null;
  minLength?: number;
  maxLength?: number;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const CustomInput = ({
  label,
  id,
  name,
  mask,
  minLength,
  maxLength,
  onChange,
  fullWidth = true,
  ...props
}: CustomInputProps) => {
  const spanRef = useRef<HTMLLabelElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const checkTruncation = () => {
      if (spanRef.current) {
        setIsTruncated(
          spanRef.current.scrollWidth > spanRef.current.clientWidth,
        );
      }
    };
    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [label]);

  const getMaskProps = () => {
    if (mask === 'telefone') {
      return { mask: [{ mask: '(00) 0000-0000' }, { mask: '(00) 00000-0000' }] };
    }
    if (mask === '00.000.000/0000-00') {
      return {
        mask: '**.***.***/****-00',
        blocks: {
          '*': { mask: /^[a-zA-Z0-9]$/ },
          '0': { mask: /^[0-9]$/ },
        },
        prepare: (str: string) => str.toUpperCase(),
      };
    }
    return mask ? { mask } : undefined;
  };

  const sharedInputProps = {
    name,
    onChange,
    minLength,
    maxLength,
    'data-testid': `input-${name}`,
  };

  return (
    <FormControl fullWidth variant="filled">
      <Tooltip
        title={isTruncated ? label : ''} // Isso agora funcionará corretamente
        arrow
        disableHoverListener={!isTruncated}
        placement="top"
      >
        <TruncatedInputLabel htmlFor={id} data-testid={`label-${name}`}>
          <span ref={spanRef} className="label-text">
            {label}
          </span>
          {props.required && !props.disabled && (
            <span className="required-asterisk">*</span>
          )}
        </TruncatedInputLabel>
      </Tooltip>

      <TextField
        {...props}
        id={id}
        name={name}
        fullWidth={fullWidth}
        variant="filled"
        size="small"
        hiddenLabel
        slotProps={{
          input: {
            inputComponent: getMaskProps() ? (MaskedTextInput as any) : undefined,
            inputProps: {
              ...sharedInputProps,
              ...(getMaskProps() || {}),
            },
          },
        }}
        onChange={!getMaskProps() ? onChange : undefined}
      />
    </FormControl>
  );
};

export default CustomInput;
